import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Footer from './footer';
import Header from './header';
import apiUrl from './base_url';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';

const QuestionPaperDownload = () => {
  const [questionPapers, setQuestionPapers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedQuestionPaperId, setSelectedQuestionPaperId] = useState(null);
  const [selectedPaperTitle, setSelectedPaperTitle] = useState('');

  const [downloadAllowed, setDownloadAllowed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    axios
      .get(apiUrl + 'serverside/question_paper.php')
      .then(response => {
        setQuestionPapers(response.data || []);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleModalOpen = (file, questionPaperId, title) => {
    setShowModal(true);
    setSelectedFile(file);
    setSelectedQuestionPaperId(questionPaperId);
    setSelectedPaperTitle(title);

    setDownloadAllowed(false);
    setErrorMessage('');
    setMobileNumber('');
  };

  const handleModalClose = () => {
    setShowModal(false);
    setMobileNumber('');
    setSelectedFile(null);
    setSelectedQuestionPaperId(null);
    setSelectedPaperTitle('');
    setDownloadAllowed(false);
    setErrorMessage('');
    setIsVerifying(false);
  };

  const handleMobileChange = event => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 10);

    setMobileNumber(value);
    setErrorMessage('');
  };

  const verifyMobileNumberAndId = () => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      setErrorMessage('Please enter a valid 10-digit mobile number.');
      return;
    }

    setIsVerifying(true);
    setErrorMessage('');

    axios
      .post(apiUrl + 'serverside/question_paper_pay_pro.php', {
        mobile: mobileNumber,
        pay_id: selectedQuestionPaperId,
      })
      .then(response => {
        if (response.data.success) {
          setDownloadAllowed(true);
          setErrorMessage('');
        } else {
          setErrorMessage(
            response.data.message || 'Access denied.'
          );
        }
      })
      .catch(error => {
        console.error('Verification Error:', error);
        setErrorMessage(
          'An error occurred while verifying your number. Please try again.'
        );
      })
      .finally(() => {
        setIsVerifying(false);
      });
  };

  const filteredPapers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return questionPapers.filter(paper =>
      String(paper.topic_name || '')
        .toLowerCase()
        .includes(query)
    );
  }, [questionPapers, searchTerm]);

  return (
    <div className="question-paper-page">

      <Header />

      {/* ================= PAGE HEADER ================= */}
      <section className="question-paper-hero">
        <div className="question-paper-hero-inner">

          <div>
            <span className="question-paper-eyebrow">
              ERWATODAY • STUDY MATERIAL
            </span>

            <h1>Download Question Papers</h1>

            <p>
              Practice with previous question papers and prepare
              confidently for your upcoming examination.
            </p>
          </div>

          <div className="question-paper-hero-icon">
            📄
          </div>

        </div>
      </section>

      {/* ================= MAIN CONTENT ================= */}
      <main className="question-paper-content">

        {/* Search */}
        <div className="question-paper-toolbar">

          <div className="question-paper-heading">
            <span>QUESTION PAPER LIBRARY</span>

            <h2>
              Available Papers
            </h2>

            <p>
              {filteredPapers.length} paper
              {filteredPapers.length !== 1 ? 's' : ''} available
            </p>
          </div>

          <div className="question-paper-search">

            <span className="question-paper-search-icon">
              🔍
            </span>

            <input
              type="text"
              placeholder="Search question papers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />

            {searchTerm && (
              <button
                type="button"
                className="question-paper-search-clear"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}

          </div>

        </div>

        {/* ================= LOADING ================= */}
        {isLoading ? (

          <div className="question-paper-loading">
            <Spinner animation="border" />

            <h5>Loading question papers...</h5>

            <p>
              Please wait while we load the available papers.
            </p>
          </div>

        ) : (

          /* ================= PAPERS ================= */
          <div className="question-paper-grid">

            {filteredPapers.length > 0 ? (

              filteredPapers.map((item, index) => (

                <article
                  key={item.id || index}
                  className="question-paper-card"
                >

                  <div className="question-paper-card-top">

                    <div className="question-paper-file-icon">
                      PDF
                    </div>

                    <span className="question-paper-number">
                      {String(index + 1).padStart(2, '0')}
                    </span>

                  </div>

                  <div className="question-paper-card-body">

                    <span className="question-paper-type">
                      QUESTION PAPER
                    </span>

                    <h3>
                      {item.topic_name}
                    </h3>

                    <p>
                      Download and practice this question paper
                      for your exam preparation.
                    </p>

                    <button
                      type="button"
                      className="question-paper-download-btn"
                      onClick={() =>
                        handleModalOpen(
                          item.file,
                          item.id,
                          item.topic_name
                        )
                      }
                    >
                      <span>
                        Download Paper
                      </span>

                      <span className="question-paper-arrow">
                        →
                      </span>
                    </button>

                  </div>

                </article>

              ))

            ) : (

              <div className="question-paper-empty">

                <div className="question-paper-empty-icon">
                  🔍
                </div>

                <h3>
                  No Question Papers Found
                </h3>

                <p>
                  Try searching with a different keyword.
                </p>

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="question-paper-clear-btn"
                  >
                    Clear Search
                  </button>
                )}

              </div>

            )}

          </div>
        )}

      </main>

      {/* ================= VERIFICATION MODAL ================= */}
      <Modal
        show={showModal}
        onHide={handleModalClose}
        centered
        className="question-paper-modal"
      >

        <Modal.Header closeButton>

          <div className="question-paper-modal-title">

            <div className="question-paper-modal-icon">
              📱
            </div>

            <div>
              <span>SECURE DOWNLOAD</span>

              <Modal.Title>
                Verify Mobile Number
              </Modal.Title>
            </div>

          </div>

        </Modal.Header>

        <Modal.Body>

          <div className="question-paper-selected">

            <span>SELECTED PAPER</span>

            <strong>
              {selectedPaperTitle}
            </strong>

          </div>

          <div className="question-paper-verification">

            <h5>
              Enter your mobile number
            </h5>

            <p>
              Enter the mobile number associated with your
              payment to verify download access.
            </p>

            <Form.Group>

              <Form.Label>
                Mobile Number
              </Form.Label>

              <div className="mobile-input-wrapper">

                <span className="mobile-prefix">
                  +91
                </span>

                <Form.Control
                  type="tel"
                  inputMode="numeric"
                  maxLength="10"
                  value={mobileNumber}
                  onChange={handleMobileChange}
                  placeholder="Enter 10-digit mobile number"
                  className="mobile-number-input"
                  disabled={isVerifying || downloadAllowed}
                />

              </div>

            </Form.Group>

            {errorMessage && (

              <div className="question-paper-error">
                <span>!</span>
                {errorMessage}
              </div>

            )}

            {downloadAllowed && (

              <div className="question-paper-success">

                <div className="success-icon">
                  ✓
                </div>

                <div>
                  <strong>
                    Verification successful
                  </strong>

                  <span>
                    Your question paper is ready to download.
                  </span>
                </div>

              </div>

            )}

          </div>

        </Modal.Body>

        <Modal.Footer>

          {!downloadAllowed ? (

            <Button
              className="question-paper-verify-btn"
              onClick={verifyMobileNumberAndId}
              disabled={isVerifying}
            >

              {isVerifying ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />

                  Verifying...
                </>
              ) : (
                <>
                  Verify & Continue
                  <span>→</span>
                </>
              )}

            </Button>

          ) : (

            <a
              href={selectedFile}
              download
              className="question-paper-final-download"
            >
              Download Now
              <span>↓</span>
            </a>

          )}

          <Button
            variant="light"
            className="question-paper-close-btn"
            onClick={handleModalClose}
          >
            Close
          </Button>

        </Modal.Footer>

      </Modal>

      <Footer />

      {/* ================= PAGE CSS ================= */}
      <style>{`

        .question-paper-page {
          min-height: 100vh;
          background: #f6f8fc;
          color: #172033;
          overflow-x: hidden;
        }

        .question-paper-hero {
          background:
            linear-gradient(135deg, #0f2d62 0%, #164a9c 55%, #2563eb 100%);
          color: #fff;
          padding: 58px 20px;
        }

        .question-paper-hero-inner {
          max-width: 1180px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 30px;
        }

        .question-paper-eyebrow {
          display: inline-block;
          margin-bottom: 12px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .16em;
          opacity: .75;
        }

        .question-paper-hero h1 {
          margin: 0;
          font-size: clamp(30px, 4vw, 48px);
          font-weight: 850;
          letter-spacing: -.03em;
        }

        .question-paper-hero p {
          max-width: 620px;
          margin: 15px 0 0;
          color: rgba(255,255,255,.78);
          font-size: 15px;
          line-height: 1.7;
        }

        .question-paper-hero-icon {
          width: 100px;
          height: 100px;
          flex: 0 0 100px;
          display: grid;
          place-items: center;
          border: 1px solid rgba(255,255,255,.2);
          border-radius: 26px;
          background: rgba(255,255,255,.1);
          font-size: 42px;
        }

        .question-paper-content {
          max-width: 1180px;
          margin: 0 auto;
          padding: 42px 20px 65px;
        }

        .question-paper-toolbar {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 30px;
          margin-bottom: 30px;
        }

        .question-paper-heading span {
          color: #2563eb;
          font-size: 10px;
          font-weight: 850;
          letter-spacing: .15em;
        }

        .question-paper-heading h2 {
          margin: 5px 0;
          font-size: 28px;
          font-weight: 800;
        }

        .question-paper-heading p {
          margin: 0;
          color: #94a3b8;
          font-size: 13px;
        }

        .question-paper-search {
          position: relative;
          width: min(390px, 100%);
        }

        .question-paper-search input {
          width: 100%;
          height: 48px;
          padding: 0 45px;
          border: 1px solid #e2e8f0;
          border-radius: 13px;
          outline: none;
          background: #fff;
          color: #1e293b;
          box-shadow: 0 5px 18px rgba(15,23,42,.04);
          transition: .2s ease;
        }

        .question-paper-search input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,.09);
        }

        .question-paper-search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 15px;
          z-index: 2;
        }

        .question-paper-search-clear {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border: 0;
          border-radius: 50%;
          background: #f1f5f9;
          color: #64748b;
          cursor: pointer;
        }

        .question-paper-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
        }

        .question-paper-card {
          overflow: hidden;
          border: 1px solid #e8edf4;
          border-radius: 18px;
          background: #fff;
          box-shadow: 0 7px 25px rgba(15,23,42,.045);
          transition: transform .2s ease, box-shadow .2s ease;
        }

        .question-paper-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 18px 40px rgba(15,23,42,.10);
        }

        .question-paper-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 20px 0;
        }

        .question-paper-file-icon {
          width: 48px;
          height: 48px;
          display: grid;
          place-items: center;
          border-radius: 13px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 12px;
          font-weight: 900;
        }

        .question-paper-number {
          color: #cbd5e1;
          font-size: 12px;
          font-weight: 800;
        }

        .question-paper-card-body {
          padding: 20px;
        }

        .question-paper-type {
          color: #94a3b8;
          font-size: 9px;
          font-weight: 850;
          letter-spacing: .13em;
        }

        .question-paper-card h3 {
          min-height: 50px;
          margin: 8px 0;
          color: #172033;
          font-size: 19px;
          font-weight: 800;
          line-height: 1.35;
        }

        .question-paper-card p {
          min-height: 43px;
          margin: 0 0 20px;
          color: #64748b;
          font-size: 12px;
          line-height: 1.65;
        }

        .question-paper-download-btn {
          width: 100%;
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 15px;
          border: 0;
          border-radius: 10px;
          background: #2563eb;
          color: #fff;
          font-size: 12px;
          font-weight: 750;
          cursor: pointer;
          transition: .2s ease;
        }

        .question-paper-download-btn:hover {
          background: #1d4ed8;
        }

        .question-paper-arrow {
          font-size: 17px;
        }

        .question-paper-loading,
        .question-paper-empty {
          min-height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          text-align: center;
          border: 1px dashed #d9e1ec;
          border-radius: 18px;
          background: #fff;
        }

        .question-paper-loading h5,
        .question-paper-empty h3 {
          margin: 15px 0 5px;
          font-weight: 800;
        }

        .question-paper-loading p,
        .question-paper-empty p {
          margin: 0;
          color: #94a3b8;
          font-size: 13px;
        }

        .question-paper-empty-icon {
          width: 58px;
          height: 58px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: #f1f5f9;
          font-size: 23px;
        }

        .question-paper-clear-btn {
          margin-top: 18px;
          padding: 9px 18px;
          border: 0;
          border-radius: 9px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 12px;
          font-weight: 750;
          cursor: pointer;
        }

        .question-paper-modal .modal-content {
          overflow: hidden;
          border: 0;
          border-radius: 18px;
          box-shadow: 0 25px 70px rgba(15,23,42,.2);
        }

        .question-paper-modal .modal-header {
          padding: 20px 22px;
          border-bottom: 1px solid #eef2f7;
        }

        .question-paper-modal-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .question-paper-modal-icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 11px;
          background: #eff6ff;
          font-size: 18px;
        }

        .question-paper-modal-title > div:last-child span {
          display: block;
          color: #94a3b8;
          font-size: 8px;
          font-weight: 850;
          letter-spacing: .12em;
        }

        .question-paper-modal-title .modal-title {
          margin-top: 3px;
          font-size: 18px;
          font-weight: 800;
        }

        .question-paper-modal .modal-body {
          padding: 22px;
        }

        .question-paper-selected {
          padding: 13px 15px;
          margin-bottom: 22px;
          border: 1px solid #e4ebf4;
          border-radius: 11px;
          background: #f8fafc;
        }

        .question-paper-selected span {
          display: block;
          margin-bottom: 4px;
          color: #94a3b8;
          font-size: 8px;
          font-weight: 850;
          letter-spacing: .12em;
        }

        .question-paper-selected strong {
          display: block;
          color: #334155;
          font-size: 13px;
        }

        .question-paper-verification h5 {
          margin-bottom: 5px;
          font-size: 15px;
          font-weight: 800;
        }

        .question-paper-verification p {
          margin-bottom: 18px;
          color: #64748b;
          font-size: 12px;
          line-height: 1.6;
        }

        .question-paper-verification .form-label {
          color: #334155;
          font-size: 11px;
          font-weight: 750;
        }

        .mobile-input-wrapper {
          position: relative;
        }

        .mobile-prefix {
          position: absolute;
          z-index: 2;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
          font-size: 13px;
          font-weight: 700;
        }

        .mobile-number-input {
          height: 47px;
          padding-left: 48px;
          border-radius: 10px;
        }

        .question-paper-error {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          padding: 10px 12px;
          border-radius: 9px;
          background: #fff1f2;
          color: #be123c;
          font-size: 11px;
        }

        .question-paper-error span {
          width: 18px;
          height: 18px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: #ffe4e6;
          font-weight: 800;
        }

        .question-paper-success {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 15px;
          padding: 12px;
          border-radius: 10px;
          background: #ecfdf5;
          color: #047857;
        }

        .success-icon {
          width: 30px;
          height: 30px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: #d1fae5;
          font-weight: 900;
        }

        .question-paper-success strong,
        .question-paper-success span {
          display: block;
        }

        .question-paper-success strong {
          font-size: 12px;
        }

        .question-paper-success span {
          margin-top: 2px;
          font-size: 10px;
        }

        .question-paper-modal .modal-footer {
          padding: 15px 22px 20px;
          border-top: 1px solid #eef2f7;
        }

        .question-paper-verify-btn,
        .question-paper-final-download {
          flex: 1;
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          border: 0;
          border-radius: 10px;
          background: #2563eb;
          color: #fff !important;
          font-size: 12px;
          font-weight: 750;
          text-decoration: none !important;
        }

        .question-paper-verify-btn:hover,
        .question-paper-final-download:hover {
          background: #1d4ed8;
        }

        .question-paper-close-btn {
          min-height: 44px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 700;
        }

        @media (max-width: 991px) {
          .question-paper-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .question-paper-toolbar {
            align-items: stretch;
            flex-direction: column;
          }

          .question-paper-search {
            width: 100%;
          }
        }

        @media (max-width: 575px) {
          .question-paper-hero {
            padding: 40px 16px;
          }

          .question-paper-hero-inner {
            align-items: flex-start;
          }

          .question-paper-hero-icon {
            display: none;
          }

          .question-paper-content {
            padding: 30px 14px 45px;
          }

          .question-paper-grid {
            grid-template-columns: 1fr;
          }

          .question-paper-card h3,
          .question-paper-card p {
            min-height: auto;
          }

          .question-paper-modal .modal-footer {
            flex-direction: column;
          }

          .question-paper-verify-btn,
          .question-paper-final-download,
          .question-paper-close-btn {
            width: 100%;
            flex: none;
          }
        }

      `}</style>
    </div>
  );
};

export default QuestionPaperDownload;